import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { VariableExtension } from '@/lib/tiptap-variable-extension';
import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon, 
  List, 
  ListOrdered, 
  Heading1, 
  Heading2, 
  Heading3,
  Quote,
  Undo,
  Redo,
  Link as LinkIcon,
  Unlink,
  Palette
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Toggle } from '@/components/ui/toggle';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useState, useCallback, useEffect } from 'react';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  className?: string;
  onInsertVariable?: (position: number) => void;
  onEditorReady?: (editor: Editor) => void;
  onFocus?: () => void;
  onBlur?: (position: number) => void;
  onSelectionUpdate?: (position: number) => void;
}

const MenuBar = ({ editor }: { editor: Editor | null }) => {
  const [linkUrl, setLinkUrl] = useState('');
  const [isLinkPopoverOpen, setIsLinkPopoverOpen] = useState(false);
  const [textColor, setTextColor] = useState('#000000');

  const addLink = useCallback(() => {
    if (!editor) return;
    if (linkUrl) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: linkUrl })
        .run();
      setLinkUrl('');
      setIsLinkPopoverOpen(false);
    }
  }, [editor, linkUrl]);

  const removeLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().unsetLink().run();
  }, [editor]);

  const setColor = useCallback((color: string) => {
    if (!editor) return;
    editor.chain().focus().setColor(color).run();
    setTextColor(color);
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="border-b bg-muted/30 p-2 flex flex-wrap gap-1 items-center">
      {/* Text Formatting */}
      <Toggle
        size="sm"
        pressed={editor.isActive('bold')}
        onPressedChange={() => editor.chain().focus().toggleBold().run()}
        aria-label="Toggle bold"
      >
        <Bold className="h-4 w-4" />
      </Toggle>
      
      <Toggle
        size="sm"
        pressed={editor.isActive('italic')}
        onPressedChange={() => editor.chain().focus().toggleItalic().run()}
        aria-label="Toggle italic"
      >
        <Italic className="h-4 w-4" />
      </Toggle>
      
      <Toggle
        size="sm"
        pressed={editor.isActive('underline')}
        onPressedChange={() => editor.chain().focus().toggleUnderline().run()}
        aria-label="Toggle underline"
      >
        <UnderlineIcon className="h-4 w-4" />
      </Toggle>

      <Separator orientation="vertical" className="h-6" />

      {/* Headings */}
      <Toggle
        size="sm"
        pressed={editor.isActive('heading', { level: 1 })}
        onPressedChange={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        aria-label="Heading 1"
      >
        <Heading1 className="h-4 w-4" />
      </Toggle>
      
      <Toggle
        size="sm"
        pressed={editor.isActive('heading', { level: 2 })}
        onPressedChange={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        aria-label="Heading 2"
      >
        <Heading2 className="h-4 w-4" />
      </Toggle>
      
      <Toggle
        size="sm"
        pressed={editor.isActive('heading', { level: 3 })}
        onPressedChange={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        aria-label="Heading 3"
      >
        <Heading3 className="h-4 w-4" />
      </Toggle>

      <Separator orientation="vertical" className="h-6" />

      {/* Lists */}
      <Toggle
        size="sm"
        pressed={editor.isActive('bulletList')}
        onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
        aria-label="Bullet list"
      >
        <List className="h-4 w-4" />
      </Toggle>
      
      <Toggle
        size="sm"
        pressed={editor.isActive('orderedList')}
        onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
        aria-label="Ordered list"
      >
        <ListOrdered className="h-4 w-4" />
      </Toggle>

      <Separator orientation="vertical" className="h-6" />

      {/* Quote */}
      <Toggle
        size="sm"
        pressed={editor.isActive('blockquote')}
        onPressedChange={() => editor.chain().focus().toggleBlockquote().run()}
        aria-label="Quote"
      >
        <Quote className="h-4 w-4" />
      </Toggle>

      <Separator orientation="vertical" className="h-6" />

      {/* Text Color */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" aria-label="Text color">
            <Palette className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64">
          <div className="space-y-2">
            <Label>Text Color</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={textColor}
                onChange={(e) => setColor(e.target.value)}
                className="h-10 w-20"
              />
              <Input
                type="text"
                value={textColor}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#000000"
                className="flex-1"
              />
            </div>
            <div className="grid grid-cols-6 gap-2 mt-2">
              {['#000000', '#374151', '#DC2626', '#EA580C', '#CA8A04', '#65A30D', '#059669', '#0891B2', '#2563EB', '#7C3AED', '#C026D3', '#DB2777'].map((color) => (
                <button
                  key={color}
                  className="w-8 h-8 rounded border-2 border-border hover:border-primary transition-colors"
                  style={{ backgroundColor: color }}
                  onClick={() => setColor(color)}
                  aria-label={`Set color to ${color}`}
                />
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Link */}
      <Popover open={isLinkPopoverOpen} onOpenChange={setIsLinkPopoverOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            aria-label="Add link"
            className={editor.isActive('link') ? 'bg-accent' : ''}
          >
            <LinkIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          <div className="space-y-2">
            <Label htmlFor="link-url">Link URL</Label>
            <div className="flex gap-2">
              <Input
                id="link-url"
                type="url"
                placeholder="https://example.com"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addLink();
                  }
                }}
              />
              <Button onClick={addLink} size="sm">
                Add
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {editor.isActive('link') && (
        <Button
          variant="ghost"
          size="sm"
          onClick={removeLink}
          aria-label="Remove link"
        >
          <Unlink className="h-4 w-4" />
        </Button>
      )}

      <Separator orientation="vertical" className="h-6" />

      {/* Undo/Redo */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        aria-label="Undo"
      >
        <Undo className="h-4 w-4" />
      </Button>
      
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        aria-label="Redo"
      >
        <Redo className="h-4 w-4" />
      </Button>
    </div>
  );
};

export function RichTextEditor({ 
  content, 
  onChange, 
  placeholder = 'Start typing...', 
  className = '',
  onInsertVariable,
  onEditorReady,
  onFocus,
  onBlur,
  onSelectionUpdate 
}: RichTextEditorProps) {
  const [isDragOver, setIsDragOver] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline cursor-pointer',
        },
      }),
      TextStyle,
      Color,
      Placeholder.configure({
        placeholder,
      }),
      VariableExtension,
    ],
    content,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[300px] p-4',
      },
      handleDrop: (view, event, slice, moved) => {
        // Check if this is a variable drop
        const variable = event.dataTransfer?.getData('application/x-template-variable');
        if (variable) {
          event.preventDefault();
          event.stopPropagation();
          
          // Clear the drag over state immediately
          setIsDragOver(false);
          
          // Get the coordinates of the drop
          const coords = { left: event.clientX, top: event.clientY };
          const pos = view.posAtCoords(coords);
          
          if (pos) {
            // Get the editor instance from the view
            const { state, dispatch } = view;
            
            // Create the variable node
            const variableNode = state.schema.nodes.variable.create({ variable });
            
            // Insert at the drop position
            const transaction = state.tr.insert(pos.pos, variableNode);
            dispatch(transaction);
            
            console.log('Variable inserted at position:', pos.pos);
          }
          
          // Prevent default TipTap drop behavior
          return true;
        }
        // Allow default behavior for other drops
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
    onCreate: ({ editor }) => {
      if (onEditorReady) {
        onEditorReady(editor);
      }
    },
    onFocus: () => {
      if (onFocus) {
        onFocus();
      }
    },
    onBlur: ({ editor }) => {
      if (onBlur) {
        onBlur(editor.state.selection.from);
      }
    },
    onSelectionUpdate: ({ editor }) => {
      if (onSelectionUpdate) {
        onSelectionUpdate(editor.state.selection.from);
      }
    },
  });

  // Update editor content when prop changes (for external updates)
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content, { emitUpdate: false }); // don't emit update event
    }
  }, [content, editor]);

  // Expose cursor position for variable insertion
  useEffect(() => {
    if (editor && onInsertVariable) {
      const handleSelectionUpdate = () => {
        const { from } = editor.state.selection;
        onInsertVariable(from);
      };
      
      editor.on('selectionUpdate', handleSelectionUpdate);
      
      return () => {
        editor.off('selectionUpdate', handleSelectionUpdate);
      };
    }
  }, [editor, onInsertVariable]);

  const handleDragEnter = (e: React.DragEvent) => {
    const types = e.dataTransfer?.types || [];
    if (types.includes('application/x-template-variable')) {
      e.preventDefault();
      setIsDragOver(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    const types = e.dataTransfer?.types || [];
    if (types.includes('application/x-template-variable')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Check if we're leaving the editor container (not just entering a child)
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    // Only clear if mouse is actually outside the container bounds
    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    // Always clear the drag over state when dropping
    setIsDragOver(false);
    
    // The actual drop handling is done in editorProps.handleDrop
    // This just clears the visual state
  };

  return (
    <div 
      className={`relative border rounded-lg overflow-hidden bg-background transition-all ${
        isDragOver ? 'border-primary border-2 ring-2 ring-primary/20' : ''
      } ${className}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <MenuBar editor={editor} />
      <EditorContent editor={editor} />
      {isDragOver && (
        <div className="absolute inset-0 bg-primary/5 pointer-events-none flex items-center justify-center z-50">
          <div className="bg-background border-2 border-primary rounded-lg px-4 py-2 shadow-lg">
            <p className="text-sm font-medium text-primary">Drop variable here</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function to insert text at cursor position
export function insertTextAtCursor(editor: Editor | null, text: string) {
  if (editor) {
    editor.chain().focus().insertContent(text).run();
  }
}
